/**
 * Shared engineered-fixture specs — a BBSA-minimal-profile biped skeleton in
 * glTF node terms, T-pose-ish geometry, authored names parameterized so tests
 * can exercise reserved-character sanitization ('mixamorig:Hips') or clean
 * names ('tgt_Hips') from the same shape.
 * Depends on: build-glb types. Depended on by: adapter tests.
 */
import type { GlbNodeSpec } from './build-glb.js';

type V3 = readonly [number, number, number];

interface MinimalBone {
  readonly id: string;
  readonly parent: string | null;
  readonly translation: V3;
}

/** The 25 minimal-profile bones with plausible local-bind offsets (meters). */
const MINIMAL_BONES: readonly MinimalBone[] = [
  { id: 'Hips', parent: null, translation: [0, 1, 0] },
  { id: 'Spine', parent: 'Hips', translation: [0, 0.1, 0] },
  { id: 'Spine1', parent: 'Spine', translation: [0, 0.1, 0] },
  { id: 'Spine2', parent: 'Spine1', translation: [0, 0.1, 0] },
  { id: 'Neck', parent: 'Spine2', translation: [0, 0.1, 0] },
  { id: 'Head', parent: 'Neck', translation: [0, 0.1, 0] },
  { id: 'HeadTop_End', parent: 'Head', translation: [0, 0.15, 0] },
  ...(['Left', 'Right'] as const).flatMap((side): MinimalBone[] => {
    const s = side === 'Left' ? 1 : -1;
    return [
      { id: `${side}Shoulder`, parent: 'Spine2', translation: [s * 0.05, 0.05, 0] },
      { id: `${side}Arm`, parent: `${side}Shoulder`, translation: [s * 0.1, 0, 0] },
      { id: `${side}ForeArm`, parent: `${side}Arm`, translation: [s * 0.25, 0, 0] },
      { id: `${side}Hand`, parent: `${side}ForeArm`, translation: [s * 0.25, 0, 0] },
      { id: `${side}UpLeg`, parent: 'Hips', translation: [s * 0.1, -0.05, 0] },
      { id: `${side}Leg`, parent: `${side}UpLeg`, translation: [0, -0.4, 0] },
      { id: `${side}Foot`, parent: `${side}Leg`, translation: [0, -0.45, 0] },
      { id: `${side}ToeBase`, parent: `${side}Foot`, translation: [0, -0.05, 0.1] },
      { id: `${side}Toe_End`, parent: `${side}ToeBase`, translation: [0, 0, 0.08] },
    ];
  }),
];

/**
 * glTF node specs for the minimal biped. `name(id)` produces the AUTHORED
 * name; node index === MINIMAL_BONES index, so channels can target by id.
 */
export function minimalBipedNodes(name: (id: string) => string): GlbNodeSpec[] {
  const indexOf = new Map(MINIMAL_BONES.map((b, i) => [b.id, i]));
  return MINIMAL_BONES.map((b) => ({
    name: name(b.id),
    parent: b.parent === null ? null : (indexOf.get(b.parent) as number),
    translation: b.translation,
  }));
}

export function minimalBoneIndex(id: string): number {
  const index = MINIMAL_BONES.findIndex((b) => b.id === id);
  if (index === -1) throw new Error(`no minimal bone "${id}"`);
  return index;
}

export const MINIMAL_BONE_IDS: readonly string[] = MINIMAL_BONES.map((b) => b.id);
